import { Badge } from "./ui/badge";
import { Label } from "./ui/label";

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-6 fade-up">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const StatCard = ({ label, value, hint, tone = "slate", icon: Icon, testid }) => {
  const tones = { slate: "text-slate-900", green: "text-emerald-700", amber: "text-amber-600", red: "text-red-600", blue: "text-blue-600" };
  return (
    <div className="rounded-xl border bg-card p-4 sm:p-5 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className={`mt-2 text-2xl font-bold font-display ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
};

const STATUS = {
  verified: "bg-emerald-100 text-emerald-800", completed: "bg-emerald-100 text-emerald-800", ok: "bg-emerald-100 text-emerald-800",
  matched: "bg-emerald-100 text-emerald-800", delivered: "bg-emerald-100 text-emerald-800", received: "bg-emerald-100 text-emerald-800",
  active: "bg-emerald-100 text-emerald-800",
  awaiting_verification: "bg-blue-100 text-blue-800", queued: "bg-blue-100 text-blue-800", sending: "bg-blue-100 text-blue-800",
  accepted: "bg-blue-100 text-blue-800", ordered: "bg-blue-100 text-blue-800", picking: "bg-blue-100 text-blue-800", configured: "bg-blue-100 text-blue-800",
  retry_wait: "bg-amber-100 text-amber-800", partial: "bg-amber-100 text-amber-800", mismatch: "bg-amber-100 text-amber-800",
  partially_received: "bg-amber-100 text-amber-800", partially_returned: "bg-amber-100 text-amber-800", packed: "bg-amber-100 text-amber-800",
  unverified: "bg-slate-200 text-slate-700", unchecked: "bg-slate-200 text-slate-700", never: "bg-slate-200 text-slate-700",
  dispatched: "bg-indigo-100 text-indigo-800", quarantine: "bg-orange-100 text-orange-800", not_configured: "bg-slate-200 text-slate-700",
};
export const StatusBadge = ({ s, testid }) => (
  <Badge variant="outline" data-testid={testid} className={`border-0 font-medium capitalize ${STATUS[s] || (String(s).includes("fail") || ["not_found", "cancelled", "error", "returned", "expired"].includes(s) ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700")}`}>
    {String(s || "-").replace(/_/g, " ")}
  </Badge>
);

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

export const Empty = ({ text = "Nothing here yet" }) => (
  <div className="py-12 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</div>
);

export const Panel = ({ title, actions, children, className = "", testid }) => (
  <section className={`rounded-xl border bg-card fade-up ${className}`} data-testid={testid}>
    {(title || actions) && (
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
        <h3 className="text-base font-semibold">{title}</h3>
        <div className="flex gap-2">{actions}</div>
      </div>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const NativeSelect = ({ value, onChange, options, testid, className = "", placeholder }) => (
  <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    className={`h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 ${className}`}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);
